import type { Cell, ParsedSheet } from './spreadsheet';

/**
 * The pieces of a spreadsheet import that are plain arithmetic: which column
 * means what, what example to show for it, how the rows are cut up to send.
 */

/** What a column can hold, in the order the picker lists them. */
export const IMPORT_TARGETS = [
  'name',
  'brand',
  'price',
  'size',
  'sizeValue',
  'sizeUnit',
  'barcode',
  'sku',
  'salePrice',
  'saleStarts',
  'saleEnds',
  'inStock',
  'department',
  'category',
  'descriptionShort',
  'descriptionLong',
  'ingredients',
  'photo',
  'nutritionLabel',
  'location',
  'nameEs',
  'descriptionShortEs',
] as const;
export type ImportTarget = (typeof IMPORT_TARGETS)[number];

/** target → column position. A target left out is not in the file. */
export type ImportMapping = Partial<Record<ImportTarget, number>>;

export type SheetLocation = { kind: 'all' } | { kind: 'store'; storeId: string } | { kind: 'column' };

export interface ImportBlanks {
  barcode: 'none' | 'reject';
  photo: 'none' | 'ask';
  nutritionLabel: 'none' | 'ask';
}

export function targetOf(mapping: ImportMapping, column: number): ImportTarget | null {
  return IMPORT_TARGETS.find((t) => mapping[t] === column) ?? null;
}

/** One column, one meaning: choosing a meaning takes it from whichever column had it. */
export function setTarget(mapping: ImportMapping, column: number, target: ImportTarget | null): ImportMapping {
  const next: ImportMapping = {};
  for (const t of IMPORT_TARGETS) {
    const at = mapping[t];
    if (at !== undefined && at !== column && t !== target) next[t] = at;
  }
  if (target) next[target] = column;
  return next;
}

export type MappingProblem = 'name_column_required' | 'price_column_required' | 'size_amount_needs_unit' | 'location_column_required';

/** The same rules the server applies, so the vendor hears about them before sending anything. */
export function mappingProblem(mapping: ImportMapping, location: SheetLocation): MappingProblem | null {
  if (mapping.name === undefined) return 'name_column_required';
  if (mapping.price === undefined) return 'price_column_required';
  if (mapping.sizeValue !== undefined && mapping.sizeUnit === undefined) return 'size_amount_needs_unit';
  if (location.kind === 'column' && mapping.location === undefined) return 'location_column_required';
  return null;
}

/** What shoppers need that this file has no column for — asked about after the import instead. */
export const ASKED_LATER = ['size', 'department', 'descriptionShort', 'ingredients', 'photo', 'nutritionLabel'] as const;
export type AskedLater = (typeof ASKED_LATER)[number];

export function notInFile(mapping: ImportMapping): AskedLater[] {
  return ASKED_LATER.filter((t) => (t === 'size' ? mapping.size === undefined && mapping.sizeUnit === undefined : mapping[t] === undefined));
}

export interface ColumnExample {
  /** The first thing found in the column; null when every row is blank. */
  example: string | null;
  blank: number;
  total: number;
}

export function columnExample(sheets: ParsedSheet[], column: number): ColumnExample {
  let example: string | null = null;
  let blank = 0;
  let total = 0;
  for (const sheet of sheets) {
    for (const row of sheet.rows) {
      total++;
      const cell = row.c[column];
      if (cell === null || cell === undefined) blank++;
      else if (example === null) example = String(cell).slice(0, 60);
    }
  }
  return { example, blank, total };
}

/** Tabs with the same headers are one table as far as "what does this column hold" goes. */
export function sheetGroups(sheets: ParsedSheet[]): number[][] {
  const groups = new Map<string, number[]>();
  sheets.forEach((s, i) => {
    const key = JSON.stringify(s.headers.map((h) => h.trim().toLowerCase()));
    groups.set(key, [...(groups.get(key) ?? []), i]);
  });
  return [...groups.values()];
}

export interface RowPiece {
  offset: number;
  rows: { s: number; r: number; c: Cell[] }[];
}

const MAX_PIECE_ROWS = 400;
const MAX_PIECE_CHARS = 1_000_000;

/** The file's rows in pieces small enough for one request each. */
export function rowPieces(sheets: ParsedSheet[]): RowPiece[] {
  const pieces: RowPiece[] = [];
  let piece: RowPiece = { offset: 0, rows: [] };
  let chars = 0;
  let index = 0;
  sheets.forEach((sheet, s) => {
    for (const row of sheet.rows) {
      const size = JSON.stringify(row.c).length + 24;
      if (piece.rows.length > 0 && (piece.rows.length >= MAX_PIECE_ROWS || chars + size > MAX_PIECE_CHARS)) {
        pieces.push(piece);
        piece = { offset: index, rows: [] };
        chars = 0;
      }
      piece.rows.push({ s, r: row.r, c: row.c });
      chars += size;
      index++;
    }
  });
  if (piece.rows.length > 0) pieces.push(piece);
  return pieces;
}

export const rowTotal = (sheets: ParsedSheet[]) => sheets.reduce((n, s) => n + s.rows.length, 0);

export const MAX_ORIGINAL_BYTES = 25 * 1024 * 1024;
const ORIGINAL_EXTENSIONS = ['csv', 'tsv', 'txt', 'xlsx'];

/** The spreadsheet files we keep a copy of: the ones still in the import, of a kind we read, and not too big. */
export function originalsToKeep<T extends { name: string; size: number }>(files: T[], inImport: string[]): T[] {
  return files.filter(
    (f) => inImport.includes(f.name) && ORIGINAL_EXTENSIONS.includes(f.name.toLowerCase().split('.').pop() ?? '') && f.size > 0 && f.size <= MAX_ORIGINAL_BYTES,
  );
}

export const MAX_PHOTO_BYTES = 4 * 1024 * 1024;
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/** The photos we can take, and how many were left out for being too big or not a photo. */
export function usablePhotos<T extends { type: string; size: number }>(files: T[]): { photos: T[]; skipped: number } {
  const photos = files.filter((f) => PHOTO_TYPES.includes(f.type) && f.size > 0 && f.size <= MAX_PHOTO_BYTES);
  return { photos, skipped: files.length - photos.length };
}

function csvField(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export interface FixRow {
  sheetIndex: number;
  file: string | null;
  sheet: string | null;
  rowNumber: number;
  reason: string;
  cells: unknown[];
}

/**
 * The rows we could not read, as a file the vendor can correct and send back:
 * their own columns in their own order, with what to fix in front. Tabs with
 * different columns each get their own header line.
 */
export function fixCsv(rows: FixRow[], headers: { headers: string[] }[], labels: { reason: string; tab: string; row: string }): string {
  const lines: string[] = [];
  let lastSheet = -1;
  for (const row of rows) {
    if (row.sheetIndex !== lastSheet) {
      if (lines.length > 0) lines.push('');
      lines.push([labels.reason, labels.tab, labels.row, ...(headers[row.sheetIndex]?.headers ?? [])].map(csvField).join(','));
      lastSheet = row.sheetIndex;
    }
    lines.push([row.reason, row.sheet || row.file || '', row.rowNumber, ...row.cells].map(csvField).join(','));
  }
  // A byte-order mark so a spreadsheet opens accents as accents.
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}
